/* ==========================================================================
   SPICY HOME - MOTION SYSTEM
   Reveals, parallax and the rouge progress marker.

   This file owns no scroll loop and no scroll listener. It subscribes to
   spicy-scroll.js and receives cached metrics once per frame.

   The performance rule, and the reason the previous version felt laggy:
   a scroll callback may only WRITE. Every measurement -- offsets, heights,
   document length -- happens on resize and is cached. Reading layout inside
   a scroll frame forces synchronous layout, and doing it once per parallax
   element per frame is what turned a smooth page into a stuttering one.

     - Slowness is the luxury. 0.8s reveals; nothing hurries.
     - One signature moment. Everything else stays calm.
     - Only transform and opacity are animated.
     - prefers-reduced-motion is honoured completely, not partially.
     - Parallax is off on touch and on small screens.
   ========================================================================== */

(function () {
  'use strict';

  if (window.__spicyMotion) return;
  window.__spicyMotion = true;

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var SMALL = window.matchMedia('(max-width: 768px)');
  var COARSE = window.matchMedia('(pointer: coarse)');

  /* ----------------------------------------------------------------------
     1. Reveals. IntersectionObserver adds .is-inview; CSS does the rest, so
        this degrades to "everything visible" if the observer never runs.
     ---------------------------------------------------------------------- */
  var revealObserver = null;

  function initReveals(root) {
    var scope = root || document;
    var items = scope.querySelectorAll('[data-sh-reveal]:not(.is-inview), [data-sh-arc]:not(.is-inview)');
    if (!items.length) return;

    if (REDUCED || !('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(items, function (el) { el.classList.add('is-inview'); });
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;

          // Siblings that share a parent come in 0.08s apart.
          var delay = parseFloat(el.getAttribute('data-sh-delay') || '0');
          if (!el.getAttribute('data-sh-delay') && el.parentElement) {
            var group = Array.prototype.filter.call(
              el.parentElement.children,
              function (c) { return c.hasAttribute('data-sh-reveal'); }
            );
            var idx = group.indexOf(el);
            if (idx > 0) delay = idx * 0.08;
          }

          if (delay > 0) el.style.transitionDelay = delay + 's';
          el.classList.add('is-inview');
          revealObserver.unobserve(el);
        });
      }, { rootMargin: '0px 0px -10% 0px', threshold: 0.1 });
    }

    Array.prototype.forEach.call(items, function (el) { revealObserver.observe(el); });
  }

  /* ----------------------------------------------------------------------
     2. Parallax. Measured on resize, applied on scroll.
        Speed is clamped to the house range; anything larger reads as a
        gimmick rather than as depth.
     ---------------------------------------------------------------------- */
  var items = [];
  var parallaxOn = false;

  function parallaxAllowed() {
    return !REDUCED && !SMALL.matches && !COARSE.matches;
  }

  function measureParallax() {
    parallaxOn = parallaxAllowed();

    var nodes = document.querySelectorAll('[data-sh-parallax]');
    items = [];

    if (!parallaxOn) {
      // Clear any offset left behind when crossing the breakpoint.
      Array.prototype.forEach.call(nodes, function (el) { el.style.removeProperty('--sh-py'); });
      return;
    }

    var y = window.pageYOffset;
    Array.prototype.forEach.call(nodes, function (el) {
      var rect = el.getBoundingClientRect();
      var speed = parseFloat(el.getAttribute('data-sh-speed') || '0.3');
      speed = Math.max(-0.8, Math.min(0.8, speed));
      items.push({
        el: el,
        speed: speed,
        centre: rect.top + y + rect.height / 2,
        half: rect.height / 2,
        last: null
      });
    });
  }

  function applyParallax(m) {
    if (!parallaxOn) return;

    var vh = m.vh || window.innerHeight;
    var viewCentre = m.scroll + vh / 2;
    var margin = vh + 200;

    for (var i = 0; i < items.length; i++) {
      var it = items[i];

      // Cheap cull using cached geometry only.
      var distance = it.centre - viewCentre;
      if (distance > margin + it.half || distance < -(margin + it.half)) continue;

      var value = ((distance / vh) * it.speed * -100).toFixed(2);
      if (value === it.last) continue;   // skip redundant style writes
      it.last = value;
      it.el.style.setProperty('--sh-py', value + 'px');
    }
  }

  /* ----------------------------------------------------------------------
     3. The rouge dot as scroll progress.
        The brand's signature device, detached from the logo, marking
        position through the page.
     ---------------------------------------------------------------------- */
  var progressEl = null;
  var progressDot = null;
  var trackHeight = 0;
  var lastDot = null;
  var progressVisible = false;

  function initProgress() {
    if (REDUCED || SMALL.matches || COARSE.matches) return;
    if (document.querySelector('.sh-progress')) return;

    progressEl = document.createElement('div');
    progressEl.className = 'sh-progress';
    progressEl.setAttribute('aria-hidden', 'true');
    progressEl.innerHTML = '<div class="sh-progress__track"><span class="sh-progress__dot"></span></div>';
    document.body.appendChild(progressEl);

    progressDot = progressEl.querySelector('.sh-progress__dot');
    trackHeight = progressEl.querySelector('.sh-progress__track').offsetHeight;
  }

  function applyProgress(m) {
    if (!progressDot) return;

    var pct = Math.max(0, Math.min(1, m.progress || 0));
    var value = (pct * trackHeight).toFixed(1);

    if (value !== lastDot) {
      lastDot = value;
      progressDot.style.transform = 'translate3d(0,' + value + 'px,0)';
    }

    // Appears only once the visitor has committed to scrolling.
    var shouldShow = m.scroll > (m.vh || window.innerHeight) * 0.4;
    if (shouldShow !== progressVisible) {
      progressVisible = shouldShow;
      progressEl.classList.toggle('is-visible', shouldShow);
    }
  }

  /* ----------------------------------------------------------------------
     4. Boot
     ---------------------------------------------------------------------- */
  function measureAll() {
    if (progressEl) {
      var track = progressEl.querySelector('.sh-progress__track');
      if (track) trackHeight = track.offsetHeight;
    }
    measureParallax();
  }

  function boot() {
    document.documentElement.classList.add('sh-js');

    initReveals(document);
    initProgress();
    measureAll();

    if (window.gsap && window.ScrollTrigger) {
      window.gsap.registerPlugin(window.ScrollTrigger);
    }

    // Everything below is write-only. No layout is read here.
    if (window.SpicyScroll) {
      window.SpicyScroll.onScroll(function (m) {
        applyProgress(m);
        applyParallax(m);
      });
    }

    var t = null;
    window.addEventListener('resize', function () {
      window.clearTimeout(t);
      t = window.setTimeout(measureAll, 150);
    }, { passive: true });

    // Crossing a breakpoint changes whether parallax runs at all.
    var onMQ = function () { measureAll(); };
    if (SMALL.addEventListener) SMALL.addEventListener('change', onMQ);
    else if (SMALL.addListener) SMALL.addListener(onMQ);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  /* Theme editor: a re-rendered section brings unobserved elements and
     invalidates every cached offset below it. */
  document.addEventListener('shopify:section:load', function (e) {
    initReveals(e.target);
    measureAll();
  });

  document.addEventListener('shopify:section:unload', function () {
    measureAll();
  });

  window.SpicyMotion = {
    refresh: function () {
      initReveals(document);
      measureAll();
      if (window.SpicyScroll) window.SpicyScroll.refresh();
    },
    reduced: REDUCED,
    // Kept for compatibility with anything that reached for the instance.
    lenis: function () { return window.SpicyScroll ? window.SpicyScroll.lenis() : null; }
  };
})();
